import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { ArrowRight, BadgeCheck, CalendarClock, Check, ShieldCheck } from 'lucide-react'
import { formatEuro, formatNumber } from '@/lib/format'
import { SectionHeading } from '@/components/generals/section-heading'
import { ButtonLink } from '@/components/generals/button-link'

import {
    INCLUDED_ITEMS,
    MAILTO_BESTUUR,
    MAILTO_GESPREK,
    PILOT_FEATURES,
    PRICE_LATER,
    PRICE_PER_PUPIL,
    SCHOOL_FEATURES
} from '@/constants/leesritme'

const MIN_PUPILS = 25
const MAX_PUPILS = 2000
const DEFAULT_PUPILS = 225

export const PricingSection: FC = (): ReactNode => {
    const [pupils, setPupils] = useState(DEFAULT_PUPILS)
    const isLarge = pupils >= MAX_PUPILS
    const total = pupils * PRICE_PER_PUPIL

    return (
        <section id='prijzen' className='relative py-20 sm:py-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionHeading
                    align='center'
                    eyebrow='Prijzen'
                    title={(
                        <>
                            Eerst proberen. <span className='font-medium text-leaf italic'>Dan pas beslissen.</span>
                        </>
                    )}
                    intro='Begin met een gratis pilot in je eigen klas. Zie je dat het werkt, dan kies je voor de hele school.'
                />

                <div className='mx-auto mt-14 grid max-w-5xl gap-6 lg:grid-cols-[1fr_1.2fr]'>
                    <article className='reveal flex flex-col rounded-[2rem] border border-ink/10 bg-card p-7 shadow-soft sm:p-9'>
                        <h3 className='font-heading text-2xl font-semibold text-ink'>
                            Gratis pilot
                        </h3>

                        <p className='mt-2 text-muted-foreground'>
                            Probeer Leesritme met je klas, zonder verplichting.
                        </p>

                        <p className='mt-6 flex items-baseline gap-2'>
                            <span className='font-heading font-soft text-6xl leading-none font-semibold tracking-tight text-ink'>
                                €&nbsp;0
                            </span>
                        </p>

                        <ul className='mt-7 grid gap-3'>
                            {PILOT_FEATURES.map(feature => (
                                <li key={feature} className='flex items-start gap-3 text-ink'>
                                    <span className='mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-leaf-soft text-leaf'>
                                        <Check className='size-3.5' strokeWidth={3} aria-hidden='true' />
                                    </span>
                                    {feature}
                                </li>
                            ))}
                        </ul>

                        <div className='mt-auto pt-8'>
                            <ButtonLink href='#aanmelden' variant='outline' size='lg' className='w-full'>
                                Start gratis met je klas
                                <ArrowRight className='size-4' aria-hidden='true' />
                            </ButtonLink>
                        </div>
                    </article>

                    <article className='reveal relative flex flex-col overflow-hidden rounded-[2rem] bg-ink p-7 text-paper shadow-lift sm:p-9'>
                        <div aria-hidden='true' className='pointer-events-none absolute -top-24 -right-24 size-72 rounded-full bg-[radial-gradient(circle_at_center,rgb(246_207_93/0.28),transparent_65%)]' />

                        <div className='relative flex flex-wrap items-start justify-between gap-3'>
                            <div>
                                <h3 className='font-heading text-2xl font-semibold'>
                                    Leesritme Schoolbreed
                                </h3>

                                <p className='mt-2 text-[#c3cad8]'>
                                    Voor alle groepen van je school.
                                </p>
                            </div>

                            <span className='rounded-full bg-sun px-3 py-1 text-xs font-bold text-ink'>
                                Ontwikkelprijs
                            </span>
                        </div>

                        <p className='relative mt-6 flex flex-wrap items-baseline gap-x-3 gap-y-1'>
                            <span className='font-heading font-soft text-6xl leading-none font-semibold tracking-tight'>
                                {formatEuro(PRICE_PER_PUPIL)}
                            </span>

                            <span className='text-[#c3cad8]'>
                                per leerling per schooljaar, exclusief btw
                            </span>
                        </p>

                        <p className='relative mt-4 text-sm leading-relaxed text-[#c3cad8]'>
                            Bij aanmelding vóór 1 februari 2027, twee schooljaren vast. Daarna {formatEuro(PRICE_LATER)} per leerling.
                        </p>

                        <div className='relative mt-7 rounded-2xl bg-white/[0.07] p-5 ring-1 ring-white/10'>
                            <div className='flex items-center justify-between gap-4'>
                                <label htmlFor='leerlingen' className='text-sm font-bold'>
                                    Aantal leerlingen
                                </label>

                                <span className='font-heading text-xl font-semibold tabular-nums'>
                                    {isLarge ? `${formatNumber(MAX_PUPILS)}+` : formatNumber(pupils)}
                                </span>
                            </div>

                            <input
                                id='leerlingen'
                                type='range'
                                min={MIN_PUPILS}
                                max={MAX_PUPILS}
                                step={5}
                                value={pupils}
                                onChange={event => setPupils(Number(event.target.value))}
                                aria-valuetext={`${pupils} leerlingen`}
                                className='mt-4 w-full cursor-pointer accent-sun'
                            />

                            <p className='mt-4 text-[#c3cad8]' aria-live='polite'>
                                {isLarge
                                    ? (
                                        <>
                                            Meer dan 2000 leerlingen? Dan maken we een <a href='#offerte' className='font-bold text-sun underline underline-offset-4'>aanbod op maat</a>.
                                        </>
                                    )
                                    : (
                                        <>
                                            <span className='font-heading text-2xl font-semibold text-paper tabular-nums'>{formatEuro(total)}</span> per schooljaar, exclusief btw
                                        </>
                                    )}
                            </p>
                        </div>

                        <ul className='relative mt-6 grid gap-3 sm:grid-cols-2'>
                            {SCHOOL_FEATURES.map(feature => (
                                <li key={feature} className='flex items-start gap-3 text-[0.9375rem]'>
                                    <span className='mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-sun text-ink'>
                                        <Check className='size-3.5' strokeWidth={3} aria-hidden='true' />
                                    </span>
                                    {feature}
                                </li>
                            ))}
                        </ul>

                        <div className='relative mt-auto flex flex-col gap-3 pt-8 sm:flex-row'>
                            <ButtonLink href='#offerte' variant='light' size='lg' className='flex-1'>
                                Vraag een offerte aan
                            </ButtonLink>

                            <ButtonLink href={MAILTO_GESPREK} variant='outline-light' size='lg' className='flex-1'>
                                <CalendarClock className='size-4' aria-hidden='true' />
                                Plan een gesprek
                            </ButtonLink>
                        </div>
                    </article>
                </div>

                <div className='mx-auto mt-6 grid max-w-5xl gap-6 lg:grid-cols-[1.4fr_1fr]'>
                    <div className='reveal rounded-[2rem] border border-ink/10 bg-card p-7 sm:p-9'>
                        <h3 className='font-heading text-xl font-semibold text-ink'>
                            Inbegrepen bij Schoolbreed, zonder meerprijs
                        </h3>

                        <ul className='mt-6 grid gap-3 sm:grid-cols-2'>
                            {INCLUDED_ITEMS.map(item => {
                                const Icon = item.icon

                                return (
                                    <li key={item.label} className='flex items-center gap-3 text-ink'>
                                        <span className='grid size-9 shrink-0 place-items-center rounded-lg bg-sun-soft text-ink'>
                                            <Icon className='size-[1.125rem]' aria-hidden='true' />
                                        </span>
                                        {item.label}
                                    </li>
                                )
                            })}
                        </ul>
                    </div>

                    <div className='grid gap-6'>
                        <div className='reveal rounded-[2rem] bg-leaf p-7 text-paper sm:p-8'>
                            <ShieldCheck className='size-8 text-sun' aria-hidden='true' />

                            <h3 className='mt-4 font-heading text-2xl font-semibold'>
                                Niet tevreden? Geld terug.
                            </h3>

                            <p className='mt-2 leading-relaxed text-white/80'>
                                Werkt het na drie maanden niet, dan krijg je je geld volledig terug en worden de gegevens verwijderd.
                            </p>
                        </div>

                        <div className='reveal rounded-[2rem] border border-ink/10 bg-card p-7 sm:p-8'>
                            <BadgeCheck className='size-7 text-leaf' aria-hidden='true' />

                            <p className='mt-3 leading-relaxed text-ink'>
                                Bestuur, of een school met meer dan 2000 leerlingen? Dan maken we een aanbod op maat.
                            </p>

                            <a
                                href={MAILTO_BESTUUR}
                                className='mt-3 inline-flex items-center gap-1.5 font-bold text-leaf underline decoration-leaf/30 underline-offset-4 hover:decoration-leaf'
                            >
                                Neem contact op
                                <ArrowRight className='size-4' aria-hidden='true' />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
